import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../environment/environment';
import { ISession } from '../../wonder-core-ng/session-store/interfaces/session.interface';
import { SessionService } from '../../wonder-core-ng/session-store/session.service';
import { selectTenant } from '../../wonder-core-ng/tenant-store/tenant.actions';
import { selectSelectedTenantId } from '../../wonder-core-ng/tenant-store/tenant.selectors';
import { TenantService } from '../../wonder-core-ng/tenant-store/tenant.service';

/** A tenant of the session; the `x-tenant-id` header of its requests is its `name`. */
export interface ISessionTenant {
  id: string;
  name: string;
}

/** The login answers with the tenants the user can enter (every tenant of the platform, for a SUPER_ADMIN) next to the usual session. */
type SessionWithTenants = ISession & { tenants?: ISessionTenant[] };

/** Key under which wonder-core-ng remembers the selected tenant across reloads. */
const SELECTED_TENANT_KEY = 'selected-tenant';

/** Key under which wonder-core-ng saves the session across reloads. */
const SESSION_KEY = 'session';

/** Whether a session can enter the tenant: it lists it among its tenants. */
const canEnter = (
  session: { tenants?: ISessionTenant[] } | null | undefined,
  name: string
): boolean => !!session?.tenants?.some((tenant) => tenant.name === name);

/**
 * Which tenant WonderBI works in. A user with a single tenant works in it; one with several (a SUPER_ADMIN
 * enters every tenant) picks one after the credentials.
 */
@Injectable({ providedIn: 'root' })
export class TenantSelectionService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly session = inject(SessionService);
  private readonly tenantService = inject(TenantService);

  private readonly selectedId = this.store.selectSignal(selectSelectedTenantId);

  /** Name of the tenant the app works in: the selected one, or the deployment's default. */
  readonly current = computed(() => String(this.selectedId() ?? environment.defaultTenant));

  /** Tenants the session can enter (empty when the login did not list them). */
  readonly tenants = computed(() => (this.session.session() as SessionWithTenants | null)?.tenants ?? []);

  /** The tenant to preselect in the picker: the default one, if the session can enter it. */
  readonly preselected = computed(
    () => this.tenants().find((tenant) => tenant.name === environment.defaultTenant)?.name ?? null
  );

  /**
   * The tenant remembered in the browser, if the session just opened can enter it. A choice left by another
   * session (a previous user, or a tenant this one cannot enter) is not worth anything.
   */
  private remembered(): string | null {
    const saved = localStorage.getItem(SELECTED_TENANT_KEY);
    return saved && canEnter(this.session.session() as SessionWithTenants | null, saved) ? saved : null;
  }

  /** Whether the session just opened has to choose a tenant: it has several and no valid choice is remembered. */
  needsChoice(): boolean {
    return this.tenants().length > 1 && !this.remembered();
  }

  /**
   * Reopening the app with a saved session goes back to the tenant the user picked (wonder-core-ng keeps it),
   * as long as the saved session can enter it.
   */
  restore(): void {
    const saved = localStorage.getItem(SELECTED_TENANT_KEY);
    if (!saved) return;
    try {
      const stored = JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null') as SessionWithTenants | null;
      if (canEnter(stored, saved)) this.tenantService.selectTenant(saved);
    } catch {
      // An unreadable saved session restores nothing.
    }
  }

  /** Ending the session forgets the picked tenant: the next one starts in the default tenant. */
  forget(): void {
    this.store.dispatch(selectTenant({ tenantId: null }));
  }

  /**
   * Loads the permissions and roles the user has in the tenant, and only then selects it (wonder-core-ng
   * remembers the selection across reloads): a failed refresh leaves nothing selected, so the picker shows again.
   */
  choose(name: string): Observable<void> {
    return this.http
      .post<Partial<ISession>>(environment.refreshRoute, null, {
        withCredentials: true,
        headers: { 'x-tenant-id': name },
      })
      .pipe(
        map((refreshed) => {
          if (!refreshed?.user || !Array.isArray(refreshed.permissions)) {
            throw new Error('The refresh did not return the session of the tenant');
          }
          this.tenantService.selectTenant(name);
          this.session.refreshSession(refreshed as ISession);
        })
      );
  }

  /**
   * Users who do not choose work in their only tenant (or in the default one when the session lists none), or
   * in the one remembered in the browser if this session can enter it. The session of the login carries the
   * roles and permissions of `public`, so the tenant's own are loaded first, as in `choose()`. A failed
   * refresh forgets the tenant and fails: nobody enters with the permissions of `public`.
   */
  enterWithoutChoice(): Observable<void> {
    const [only] = this.tenants().length === 1 ? this.tenants() : [];
    const target = this.remembered() ?? only?.name ?? environment.defaultTenant;
    return this.choose(target).pipe(
      catchError((error: unknown) => {
        this.forget();
        return throwError(() => error);
      })
    );
  }
}
